import { Canvas, useFrame } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import * as THREE from 'three';
import {
  PENALTY_GOAL,
  PENALTY_SHOT_PRESETS,
  createPenaltyShotConfig,
  simulatePenaltyShootout,
  type NetNodeFrame,
  type PenaltyShotConfig,
  type PenaltyShotPresetId,
  type PenaltySimulationResult,
} from '../physics/penaltyShootout';
import { SPORTS_BALL_PRESETS, type SportsBallId } from '../physics/sportsBall';
import './PenaltyShootout.css';

declare global {
  interface Window {
    __penaltyShootoutState?: {
      ready: boolean;
      ballId: SportsBallId;
      outcome: string;
      scored: boolean;
      entry?: { xM: number; yM: number; speedMps: number };
      peakNetDepthM: number;
      netImpacts: number;
      controls: PenaltyShotConfig;
      phase: 'aiming' | 'flying';
      power: number;
    };
  }
}

const BALL_IDS: SportsBallId[] = ['football', 'basketball', 'tennis'];
const SHOT_IDS: PenaltyShotPresetId[] = ['driven', 'curl', 'chip', 'wide'];

export default function PenaltyShootout({ onBack }: { onBack: () => void }) {
  const [config, setConfig] = useState<PenaltyShotConfig>(() => createPenaltyShotConfig('football', 'driven'));
  const [replayKey, setReplayKey] = useState(0);
  const [phase, setPhase] = useState<'aiming' | 'flying'>('aiming');
  const [charging, setCharging] = useState(false);
  const [power, setPower] = useState(0.5);
  const result = useMemo(() => simulatePenaltyShootout(config), [config, replayKey]);
  const activeBall = SPORTS_BALL_PRESETS[config.ballId];

  useEffect(() => {
    window.__penaltyShootoutState = {
      ready: result.ballFrames.length > 0 && result.netFrames.length > 0,
      ballId: config.ballId,
      outcome: result.outcome,
      scored: result.scored,
      entry: result.entry && {
        xM: result.entry.xM,
        yM: result.entry.yM,
        speedMps: result.entry.speedMps,
      },
      peakNetDepthM: result.peakNetDepthM,
      netImpacts: result.netImpacts.length,
      controls: config,
      phase,
      power,
    };
  }, [config, phase, power, result]);

  const patchConfig = (patch: Partial<PenaltyShotConfig>) => {
    setConfig((current) => ({ ...current, ...patch }));
  };

  const selectBall = (ballId: SportsBallId) => {
    setConfig((current) => createPenaltyShotConfig(ballId, current.shotPresetId));
  };

  const selectShot = (shotPresetId: PenaltyShotPresetId) => {
    setConfig((current) => createPenaltyShotConfig(current.ballId, shotPresetId));
  };

  const aimFromPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (phase !== 'aiming') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    patchConfig({
      horizontalAngleDeg: THREE.MathUtils.clamp((0.5 - x) * 26, -13, 13),
      verticalAngleDeg: THREE.MathUtils.clamp(18 - y * 19, 2, 20),
    });
  };

  const beginCharge = () => {
    if (phase !== 'aiming') return;
    setCharging(true);
  };

  const releaseShot = useCallback(() => {
    if (!charging || phase !== 'aiming') return;
    setCharging(false);
    setPhase('flying');
    setReplayKey((value) => value + 1);
  }, [charging, phase]);

  useEffect(() => {
    if (!charging) return;
    const startedAt = performance.now();
    const timer = window.setInterval(() => {
      const nextPower = Math.min(1, 0.28 + (performance.now() - startedAt) / 1150);
      setPower(nextPower);
      const baseSpeed = createPenaltyShotConfig(config.ballId, config.shotPresetId).speedMps;
      setConfig((current) => ({ ...current, speedMps: THREE.MathUtils.clamp(baseSpeed * (0.58 + nextPower * 0.66), 5, 60) }));
    }, 40);
    return () => window.clearInterval(timer);
  }, [charging, config.ballId, config.shotPresetId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (phase !== 'aiming') return;
      if (event.code === 'Space' && !event.repeat) {
        event.preventDefault();
        beginCharge();
      }
      if (event.key.toLowerCase() === 'c') {
        const index = SHOT_IDS.indexOf(config.shotPresetId);
        selectShot(SHOT_IDS[(index + 1) % SHOT_IDS.length]);
      }
      if (event.key.toLowerCase() === 'b') {
        const index = BALL_IDS.indexOf(config.ballId);
        selectBall(BALL_IDS[(index + 1) % BALL_IDS.length]);
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') releaseShot();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [config.ballId, config.shotPresetId, phase, releaseShot]);

  const resetAim = useCallback(() => {
    setPhase('aiming');
    setPower(0.5);
  }, []);

  return (
    <div className="penalty-page">
      <header className="penalty-header">
        <button type="button" className="penalty-back" onClick={onBack}>Back</button>
        <div className="penalty-title">
          <span>BOX3D BALL PHYSICS / PENALTY SHOOTOUT</span>
          <h1>Shape the strike. Watch the net answer.</h1>
        </div>
        <div className={`penalty-outcome ${result.scored ? 'scored' : 'missed'}`} data-testid="penalty-outcome">
          <span>{result.scored ? 'GOAL' : 'MISS'}</span>
          <strong>{formatOutcome(result)}</strong>
        </div>
      </header>

      <main className="penalty-main">
        <section className="penalty-stage-shell">
          <div
            className={`penalty-stage penalty-stage-${phase}`}
            data-testid="penalty-stage"
            tabIndex={0}
            onPointerMove={aimFromPointer}
            onPointerDown={beginCharge}
            onPointerUp={releaseShot}
            onPointerCancel={() => setCharging(false)}
          >
            <Canvas
              shadows={{ type: THREE.PCFSoftShadowMap }}
              camera={{ position: [0, 1.55, -14.4], fov: 52, near: 0.01, far: 80 }}
              onCreated={({ camera, gl }) => {
                gl.setClearColor('#071012');
                camera.lookAt(0, 1.12, 0);
              }}
            >
              <PenaltyScene
                key={replayKey}
                result={result}
                phase={phase}
                onComplete={resetAim}
              />
            </Canvas>
            <div className="penalty-vignette" />
            <div className="penalty-decision-ui" aria-live="polite">
              <div className="penalty-shot-state">
                <span>{PENALTY_SHOT_PRESETS[config.shotPresetId].label}</span>
                <strong>{config.horizontalAngleDeg.toFixed(1)}° aim · {config.verticalAngleDeg.toFixed(1)}° lift</strong>
              </div>
              <div className="penalty-speed-state">
                <strong>{config.speedMps.toFixed(1)}</strong>
                <span>m/s</span>
              </div>
              <div className="penalty-control-hint">
                {phase === 'aiming' ? 'MOVE TO AIM · HOLD TO POWER · RELEASE TO SHOOT' : 'SHOT IN FLIGHT'}
              </div>
              <div className="penalty-power">
                <span>POWER</span>
                <div><i data-testid="penalty-power-fill" style={{ width: `${power * 100}%` }} /></div>
              </div>
              <div className="penalty-cycle-hint">
                <span><kbd>C</kbd> {PENALTY_SHOT_PRESETS[config.shotPresetId].label}</span>
                <span><kbd>B</kbd> {activeBall.label}</span>
              </div>
            </div>
          </div>

          <div className="penalty-readout" data-testid="penalty-readout">
            <Metric label="Entry" value={result.entry ? `${result.entry.xM.toFixed(2)} x / ${result.entry.yM.toFixed(2)} m` : '--'} />
            <Metric label="Entry speed" value={result.entry ? `${result.entry.speedMps.toFixed(1)} m/s` : '--'} />
            <Metric label="Net impulse" value={result.netImpacts[0] ? `${result.netImpacts[0].impulseNs.toFixed(1)} Ns` : '--'} />
            <Metric label="Net depth" value={`${result.peakNetDepthM.toFixed(2)} m`} />
            <Metric label="Shot" value={PENALTY_SHOT_PRESETS[config.shotPresetId].label} />
            <Metric label="Ball" value={`${activeBall.massKg.toFixed(3)} kg / ${(activeBall.radiusM * 100).toFixed(1)} cm`} />
          </div>
        </section>
      </main>
    </div>
  );
}

function PenaltyScene({
  result,
  phase,
  onComplete,
}: {
  result: PenaltySimulationResult;
  phase: 'aiming' | 'flying';
  onComplete: () => void;
}) {
  const ballRef = useRef<THREE.Group | null>(null);
  const haloRef = useRef<THREE.Mesh | null>(null);
  const netRef = useRef<THREE.LineSegments | null>(null);
  const elapsedRef = useRef(0);
  const completedRef = useRef(false);
  const ballColor = result.spec.visual.color;
  const visualRadius = Math.max(result.spec.radiusM * 1.85, result.spec.id === 'tennis' ? 0.11 : 0.16);
  const trailGeometry = useMemo(() => makeTrailGeometry(result), [result]);
  const trailLine = useMemo(() => new THREE.Line(
    trailGeometry,
    new THREE.LineBasicMaterial({ color: ballColor, transparent: true, opacity: 0.76 }),
  ), [ballColor, trailGeometry]);
  const previewLine = useMemo(() => makePreviewLine(result, ballColor), [ballColor, result]);
  const netGeometry = useMemo(() => makeNetGeometry(result.netFrames[0] ?? []), [result]);
  const lastFrameTime = result.ballFrames[result.ballFrames.length - 1]?.t ?? 0;

  useFrame((_, delta) => {
    if (phase === 'aiming') {
      elapsedRef.current = 0;
      completedRef.current = false;
    } else {
      elapsedRef.current = Math.min(lastFrameTime, elapsedRef.current + delta);
      if (elapsedRef.current >= lastFrameTime && !completedRef.current) {
        completedRef.current = true;
        window.setTimeout(onComplete, 700);
      }
    }
    const index = frameIndex(result, elapsedRef.current);
    const ballFrame = result.ballFrames[index] ?? result.ballFrames[result.ballFrames.length - 1];
    if (ballRef.current && ballFrame) {
      ballRef.current.position.set(ballFrame.position[0], Math.max(ballFrame.position[1], visualRadius), ballFrame.position[2]);
      ballRef.current.rotation.x -= delta * 9;
      ballRef.current.rotation.y += delta * 4;
    }
    if (haloRef.current && ballFrame) {
      haloRef.current.position.set(ballFrame.position[0], Math.max(ballFrame.position[1], visualRadius), ballFrame.position[2]);
    }
    trailLine.visible = phase === 'flying';
    trailLine.geometry.setDrawRange(0, Math.max(2, index));
    previewLine.visible = phase === 'aiming';
    if (netRef.current && result.netFrames.length > 0) {
      const netIndex = Math.min(result.netFrames.length - 1, Math.floor(index / 1));
      updateNetGeometry(netRef.current.geometry as THREE.BufferGeometry, result.netFrames[netIndex]);
    }
  });

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[0, 7, -5]} intensity={3.2} castShadow shadow-mapSize={[2048, 2048]} />
      <pointLight position={[-3.5, 1.8, -5]} color="#66ffe1" intensity={18} distance={12} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[13, 26]} />
        <meshStandardMaterial color="#1d6f43" roughness={0.9} />
      </mesh>
      <gridHelper args={[13, 26, '#67ffcf', '#1b4c42']} position={[0, 0.01, -5.4]} />
      <GoalFrame />
      <primitive object={previewLine} />
      <primitive object={trailLine} />
      <lineSegments ref={netRef} geometry={netGeometry}>
        <lineBasicMaterial color="#d9fff7" transparent opacity={0.74} />
      </lineSegments>
      <group ref={ballRef}>
        <mesh castShadow>
          <sphereGeometry args={[visualRadius, 36, 24]} />
          <meshStandardMaterial color={ballColor} roughness={0.38} emissive={ballColor} emissiveIntensity={0.08} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[visualRadius * 0.78, visualRadius * 0.035, 8, 48]} />
          <meshBasicMaterial color={result.spec.visual.accent} transparent opacity={0.72} />
        </mesh>
      </group>
      <mesh ref={haloRef} renderOrder={4}>
        <sphereGeometry args={[visualRadius * 1.55, 24, 16]} />
        <meshBasicMaterial color={ballColor} wireframe transparent opacity={0.2} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0.01, -PENALTY_GOAL.penaltyDistanceM]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.15, 0.19, 48]} />
        <meshBasicMaterial color="#f7f3e8" />
      </mesh>
      {phase === 'aiming' && result.entry && (
        <group position={[result.entry.xM, result.entry.yM, 0.03]}>
          <mesh renderOrder={8}>
            <ringGeometry args={[0.26, 0.38, 48]} />
            <meshBasicMaterial color="#ffe970" transparent opacity={0.96} depthTest={false} />
          </mesh>
          <mesh renderOrder={8}>
            <boxGeometry args={[0.82, 0.025, 0.015]} />
            <meshBasicMaterial color="#ffe970" depthTest={false} />
          </mesh>
          <mesh renderOrder={8}>
            <boxGeometry args={[0.025, 0.82, 0.015]} />
            <meshBasicMaterial color="#ffe970" depthTest={false} />
          </mesh>
          <mesh renderOrder={8}>
            <ringGeometry args={[0.04, 0.065, 24]} />
            <meshBasicMaterial color="#ffffff" depthTest={false} />
          </mesh>
        </group>
      )}
    </>
  );
}

function makePreviewLine(result: PenaltySimulationResult, color: string): THREE.Line {
  const cutoff = (result.entry?.timeS ?? result.ballFrames[result.ballFrames.length - 1]?.t ?? 0) + 0.08;
  const previewFrames = result.ballFrames.filter((frame) => frame.t <= cutoff);
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(previewFrames.length * 3);
  previewFrames.forEach((frame, index) => positions.set(frame.position, index * 3));
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const line = new THREE.Line(
    geometry,
    new THREE.LineDashedMaterial({ color, dashSize: 0.2, gapSize: 0.11, transparent: true, opacity: 0.88 }),
  );
  line.computeLineDistances();
  line.renderOrder = 6;
  return line;
}

function GoalFrame() {
  const halfWidth = PENALTY_GOAL.widthM / 2;
  const height = PENALTY_GOAL.heightM;
  const material = <meshStandardMaterial color="#f7f3e8" roughness={0.36} />;
  return (
    <group>
      <mesh position={[-halfWidth, height / 2, 0]} castShadow>{material}<boxGeometry args={[0.12, height, 0.12]} /></mesh>
      <mesh position={[halfWidth, height / 2, 0]} castShadow>{material}<boxGeometry args={[0.12, height, 0.12]} /></mesh>
      <mesh position={[0, height, 0]} castShadow>{material}<boxGeometry args={[PENALTY_GOAL.widthM + 0.12, 0.12, 0.12]} /></mesh>
      <mesh position={[0, 0.025, 0.18]} receiveShadow><boxGeometry args={[PENALTY_GOAL.widthM, 0.05, 0.4]} /><meshStandardMaterial color="#e9fff7" roughness={0.8} /></mesh>
    </group>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function formatOutcome(result: PenaltySimulationResult): string {
  if (result.outcome === 'saved-by-net') return 'Net caught it';
  if (result.outcome === 'goal') return 'Across the line';
  if (result.outcome === 'post') return 'Post';
  if (result.outcome === 'crossbar') return 'Crossbar';
  if (result.outcome === 'wide') return 'Wide';
  if (result.outcome === 'over') return 'Over';
  return 'Short';
}

function frameIndex(result: PenaltySimulationResult, elapsed: number): number {
  const frameDuration = result.ballFrames.length > 1 ? result.ballFrames[1].t - result.ballFrames[0].t : 1 / 80;
  return Math.min(result.ballFrames.length - 1, Math.floor(elapsed / frameDuration));
}

function makeTrailGeometry(result: PenaltySimulationResult): THREE.BufferGeometry {
  const positions = new Float32Array(result.ballFrames.length * 3);
  result.ballFrames.forEach((frame, index) => {
    positions.set(frame.position, index * 3);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setDrawRange(0, 2);
  return geometry;
}

function makeNetGeometry(frame: NetNodeFrame[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(netSegmentCount() * 2 * 3);
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  updateNetGeometry(geometry, frame);
  return geometry;
}

function updateNetGeometry(geometry: THREE.BufferGeometry, frame: NetNodeFrame[]): void {
  const attribute = geometry.getAttribute('position') as THREE.BufferAttribute;
  const positions = attribute.array as Float32Array;
  let cursor = 0;
  const columns = PENALTY_GOAL.netColumns;
  const rows = PENALTY_GOAL.netRows;
  const push = (node: NetNodeFrame) => {
    positions[cursor] = node.x;
    positions[cursor + 1] = node.y;
    positions[cursor + 2] = node.z;
    cursor += 3;
  };
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < columns - 1; col += 1) {
      push(frame[row * columns + col]);
      push(frame[row * columns + col + 1]);
    }
  }
  for (let col = 0; col < columns; col += 1) {
    for (let row = 0; row < rows - 1; row += 1) {
      push(frame[row * columns + col]);
      push(frame[(row + 1) * columns + col]);
    }
  }
  attribute.needsUpdate = true;
  geometry.computeBoundingSphere();
}

function netSegmentCount(): number {
  return PENALTY_GOAL.netRows * (PENALTY_GOAL.netColumns - 1)
    + PENALTY_GOAL.netColumns * (PENALTY_GOAL.netRows - 1);
}
